import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import MDEditor from '@uiw/react-md-editor';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { AdminOverview } from '../components/admin/AdminOverview';
import { AdminProjects } from '../components/admin/AdminProjects';
import { AdminMedia } from '../components/admin/AdminMedia';
import { AdminUsers } from '../components/admin/AdminUsers';
import { AdminDocs } from '../components/admin/AdminDocs';
import { AdminConfigs } from '../components/admin/AdminConfigs';
import { AdminChangelog } from '../components/admin/AdminChangelog';
import {
    LayoutDashboard,
    FolderOpen,
    FileText,
    Image,
    Settings,
    Plus,
    Pencil,
    Trash2,
    Save,
    X,
    LogOut,
    Sun,
    Moon,
    Upload,
    Eye,
    EyeOff,
    Loader2,
    ChevronLeft,
    Menu,
    Copy,
    Users,
    Shield,
    ShieldOff,
    Download,
    File,
    Check,
    Languages,
    Cog,
    Code,
    History,
    ThumbsUp,
    ThumbsDown
} from 'lucide-react';
import { useDocsI18n } from '../i18n';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
    const { lang, t, toggle } = useDocsI18n();
    const { user, token, logout, loading: authLoading } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const [stats, setStats] = useState({ projects: 0, documentations: 0, media: 0, users: 0 });
    const [projects, setProjects] = useState([]);
    const [docs, setDocs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [media, setMedia] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [topDocs, setTopDocs] = useState([]);
    const [totalViews, setTotalViews] = useState(0);
    const [configTemplates, setConfigTemplates] = useState([]);
    const [feedbackStats, setFeedbackStats] = useState([]);
    const [detailedAnalytics, setDetailedAnalytics] = useState([]);
    const [docHistory, setDocHistory] = useState([]);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [selectedDocForHistory, setSelectedDocForHistory] = useState(null);

    // Form states
    const [projectForm, setProjectForm] = useState({ name: '', slug: '', description: '', icon: '', color: '#00F0FF', is_admin_only: false });
    const [docForm, setDocForm] = useState({ project_id: '', title: '', slug: '', content: '', category: '', tags: '', order: 0, is_published: true, language: 'de', translation_of: '' });
    const [categoryForm, setCategoryForm] = useState({ project_id: '', name: '', slug: '', order: 0 });
    const [configForm, setConfigForm] = useState({ project_id: '', name: '', description: '', fields: [], template: '' });
    
    const [editingProject, setEditingProject] = useState(null);
    const [editingDoc, setEditingDoc] = useState(null);
    const [editingConfig, setEditingConfig] = useState(null);
    const [projectDialogOpen, setProjectDialogOpen] = useState(false);
    const [docDialogOpen, setDocDialogOpen] = useState(false);
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [configDialogOpen, setConfigDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    
    // Changelog states
    const [changelogEntries, setChangelogEntries] = useState([]);
    const [changelogForm, setChangelogForm] = useState({ title: '', description: '', type: 'update', doc_id: '', project_id: '', is_visible: true });
    const [editingChangelog, setEditingChangelog] = useState(null);
    const [changelogDialogOpen, setChangelogDialogOpen] = useState(false);
    
    // File upload states
    const [docFiles, setDocFiles] = useState([]);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadQueue, setUploadQueue] = useState([]);

    // Reset body overflow when leaving admin panel
    useEffect(() => {
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    useEffect(() => {
        if (!authLoading && !user) {
            navigate('/login');
        } else if (user && !user.is_admin) {
            navigate('/');
            toast.error(t('adminRequired'));
        }
    }, [user, authLoading, navigate]);

    useEffect(() => {
        if (user?.is_admin) {
            fetchData();
        }
    }, [user]);

    const fetchData = async () => {
        try {
            const [statsRes, projectsRes, docsRes, categoriesRes, mediaRes, usersRes, topDocsRes, totalViewsRes, configRes, feedbackRes, detailedAnalyticsRes, changelogRes] = await Promise.all([
                axios.get(`${API}/stats`),
                axios.get(`${API}/projects`),
                axios.get(`${API}/docs?published_only=false`),
                axios.get(`${API}/categories`),
                axios.get(`${API}/media`),
                axios.get(`${API}/users`),
                axios.get(`${API}/analytics/top-docs?limit=5`),
                axios.get(`${API}/analytics/total-views`),
                axios.get(`${API}/config-templates`),
                axios.get(`${API}/analytics/feedback`).catch(() => ({ data: [] })),
                axios.get(`${API}/analytics/detailed-all`).catch(() => ({ data: [] })),
                axios.get(`${API}/changelog?include_hidden=true`).catch(() => ({ data: [] }))
            ]);
            setStats(statsRes.data);
            setProjects(projectsRes.data);
            setDocs(docsRes.data);
            setCategories(categoriesRes.data);
            setMedia(mediaRes.data);
            setUsers(usersRes.data);
            setTopDocs(topDocsRes.data);
            setTotalViews(totalViewsRes.data.total_views || 0);
            setConfigTemplates(configRes.data);
            setFeedbackStats(feedbackRes.data || []);
            setDetailedAnalytics(detailedAnalyticsRes.data || []);
            setChangelogEntries(changelogRes.data || []);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error(t('loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    // Project CRUD
    const handleProjectSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingProject) {
                await axios.put(`${API}/projects/${editingProject.id}`, projectForm);
                toast.success(t('projectUpdated'));
            } else {
                await axios.post(`${API}/projects`, projectForm);
                toast.success(t('projectCreated'));
            }
            setProjectDialogOpen(false);
            resetProjectForm();
            fetchData();
        } catch (error) {
            console.error('Error saving project:', error);
            toast.error(error.response?.data?.detail || t('saveFailed'));
        }
    };

    const resetProjectForm = () => {
        setProjectForm({ name: '', slug: '', description: '', icon: '', color: '#00F0FF', is_admin_only: false });
        setEditingProject(null);
    };

    const editProject = (project) => {
        setProjectForm({
            name: project.name,
            slug: project.slug,
            description: project.description,
            icon: project.icon || '',
            color: project.color,
            is_admin_only: project.is_admin_only || false
        });
        setEditingProject(project);
        setProjectDialogOpen(true);
    };

    // Doc CRUD
    const handleDocSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...docForm,
            tags: docForm.tags ? docForm.tags.split(',').map(t => t.trim()) : []
        };
        try {
            if (editingDoc) {
                await axios.put(`${API}/docs/${editingDoc.id}`, payload);
                toast.success('Dokumentation aktualisiert');
            } else {
                await axios.post(`${API}/docs`, payload);
                toast.success('Dokumentation erstellt');
            }
            setDocDialogOpen(false);
            resetDocForm();
            fetchData();
        } catch (error) {
            console.error('Error saving doc:', error);
            toast.error(error.response?.data?.detail || t('saveFailed'));
        }
    };

    const resetDocForm = () => {
        setDocForm({ project_id: '', title: '', slug: '', content: '', category: '', tags: '', order: 0, is_published: true, language: 'de', translation_of: '' });
        setEditingDoc(null);
        setDocFiles([]);
    };

    const editDoc = (doc) => {
        setDocForm({
            project_id: doc.project_id,
            title: doc.title,
            slug: doc.slug,
            content: doc.content,
            category: doc.category,
            tags: doc.tags?.join(', ') || '',
            order: doc.order,
            is_published: doc.is_published,
            language: doc.language || 'de',
            translation_of: doc.translation_of || ''
        });
        setEditingDoc(doc);
        fetchDocFiles(doc.id);
        setDocDialogOpen(true);
    };

    // Create translation of a doc
    const createTranslation = (doc) => {
        setDocForm({
            project_id: doc.project_id,
            title: doc.title + ' (English)',
            slug: doc.slug + '-en',
            content: doc.content,
            category: doc.category,
            tags: doc.tags?.join(', ') || '',
            order: doc.order,
            is_published: false,
            language: 'en',
            translation_of: doc.id
        });
        setEditingDoc(null);
        setDocFiles([]);
        setDocDialogOpen(true);
    };

    // Create English translation of a config template
    const createConfigTranslation = async (config) => {
        try {
            // Create a copy with English descriptions
            const translatedFields = config.fields?.map(field => ({
                ...field,
                description: field.description_en || field.description,
                // Swap the descriptions so EN becomes primary
                description_de: field.description_de || field.description,
                description_en: field.description_en || field.description
            })) || [];

            const newTemplate = {
                project_id: config.project_id,
                name: config.name.replace(' DE', '').replace(' (DE)', '') + ' EN',
                description: config.description ? config.description + ' (English)' : 'English version',
                fields: translatedFields,
                template: config.template
            };

            await axios.post(`${API}/config-templates`, newTemplate);
            toast.success('Englische Version erstellt');
            fetchData();
        } catch (error) {
            console.error('Error creating config translation:', error);
            toast.error(t('translationFailed'));
        }
    };

    // Fetch files for a documentation
    const fetchDocFiles = async (docId) => {
        try {
            const response = await axios.get(`${API}/docs/${docId}/files`);
            setDocFiles(response.data);
        } catch (error) {
            console.error('Error fetching doc files:', error);
            setDocFiles([]);
        }
    };

    // Upload file to documentation
    const handleDocFileUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0 || !editingDoc) return;
        await uploadFiles(Array.from(files));
        e.target.value = '';
    };

    // Upload multiple files
    const uploadFiles = async (files) => {
        if (!editingDoc) return;
        
        const allowedExtensions = ['.lua', '.zip', '.cfg', '.json', '.xml', '.txt', '.md', '.yml', '.yaml', '.sql', '.js', '.html', '.css'];
        const validFiles = files.filter(file => {
            const ext = '.' + file.name.split('.').pop().toLowerCase();
            if (!allowedExtensions.includes(ext)) {
                toast.error(`${file.name}: Dateityp nicht erlaubt`);
                return false;
            }
            if (file.size > 25 * 1024 * 1024) {
                toast.error(t('fileTooBig', { name: file.name }));
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) return;

        setUploadingFile(true);
        setUploadQueue(validFiles.map(f => ({ name: f.name, status: 'pending' })));

        for (let i = 0; i < validFiles.length; i++) {
            const file = validFiles[i];
            setUploadQueue(prev => prev.map((item, idx) => 
                idx === i ? { ...item, status: 'uploading' } : item
            ));

            const formData = new FormData();
            formData.append('file', file);

            try {
                await axios.post(`${API}/docs/${editingDoc.id}/files`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                setUploadQueue(prev => prev.map((item, idx) => 
                    idx === i ? { ...item, status: 'done' } : item
                ));
            } catch (error) {
                console.error('Upload error:', error);
                setUploadQueue(prev => prev.map((item, idx) => 
                    idx === i ? { ...item, status: 'error' } : item
                ));
                toast.error(`${file.name}: Upload fehlgeschlagen`);
            }
        }

        setUploadingFile(false);
        setUploadQueue([]);
        fetchDocFiles(editingDoc.id);
        toast.success(`${validFiles.length} Datei(en) hochgeladen`);
    };

    // Drag & Drop handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            await uploadFiles(files);
        }
    };

    // Delete file from documentation
    const handleDocFileDelete = async (fileId) => {
        if (!editingDoc) return;
        try {
            await axios.delete(`${API}/docs/${editingDoc.id}/files/${fileId}`);
            toast.success(t('fileDeleted'));
            fetchDocFiles(editingDoc.id);
        } catch (error) {
            console.error('Delete error:', error);
            toast.error(t('deleteFailed'));
        }
    };

    // Copy file link for markdown
    const copyFileLink = (file) => {
        const markdownLink = `[📥 ${file.original_filename}](/api/docs/${editingDoc.id}/files/${file.id})`;
        navigator.clipboard.writeText(markdownLink);
        toast.success(t('linkCopied'));
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    // Category CRUD
    const handleCategorySubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/categories`, categoryForm);
            toast.success('Kategorie erstellt');
            setCategoryDialogOpen(false);
            setCategoryForm({ project_id: '', name: '', slug: '', order: 0 });
            fetchData();
        } catch (error) {
            console.error('Error saving category:', error);
            toast.error(t('saveFailed'));
        }
    };

    // Delete
    const handleDelete = async () => {
        if (!itemToDelete) return;
        try {
            await axios.delete(`${API}/${itemToDelete.type}/${itemToDelete.id}`);
            toast.success(t('deleted'));
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            fetchData();
        } catch (error) {
            console.error('Error deleting:', error);
            toast.error(t('deleteFailed'));
        }
    };

    // Config Template CRUD
    const handleConfigSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingConfig) {
                await axios.put(`${API}/config-templates/${editingConfig.id}`, configForm);
                toast.success('Config-Template aktualisiert');
            } else {
                await axios.post(`${API}/config-templates`, configForm);
                toast.success('Config-Template erstellt');
            }
            setConfigDialogOpen(false);
            resetConfigForm();
            fetchData();
        } catch (error) {
            console.error('Error saving config:', error);
            toast.error(t('saveFailed'));
        }
    };

    const resetConfigForm = () => {
        setConfigForm({ project_id: '', name: '', description: '', fields: [], template: '' });
        setEditingConfig(null);
    };

    const editConfigTemplate = (config) => {
        setConfigForm({
            project_id: config.project_id,
            name: config.name,
            description: config.description || '',
            fields: config.fields || [],
            template: config.template || ''
        });
        setEditingConfig(config);
        setConfigDialogOpen(true);
    };

    const addConfigField = () => {
        setConfigForm({
            ...configForm,
            fields: [...configForm.fields, { name: '', label: '', type: 'string', default: '', options: [], description: '' }]
        });
    };

    const updateConfigField = (index, field) => {
        const newFields = [...configForm.fields];
        newFields[index] = field;
        setConfigForm({ ...configForm, fields: newFields });
    };

    const removeConfigField = (index) => {
        setConfigForm({
            ...configForm,
            fields: configForm.fields.filter((_, i) => i !== index)
        });
    };

    const [parsedFields, setParsedFields] = useState([]);
    const [originalTemplate, setOriginalTemplate] = useState('');
    const [showFieldSelector, setShowFieldSelector] = useState(false);
    const [configLanguage, setConfigLanguage] = useState('de');

    const parseConfigFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const content = await file.text();
        
        try {
            const response = await axios.post(
                `${API}/config-templates/parse-config?language=${configLanguage}`,
                { content }
            );
            
            // Mark all fields as selected by default
            const fieldsWithSelection = response.data.fields.map(f => ({ ...f, selected: true }));
            setParsedFields(fieldsWithSelection);
            setOriginalTemplate(content);
            setShowFieldSelector(true);
            setConfigForm({ ...configForm, name: file.name });
            
            toast.success(`${response.data.fields.length} Felder erkannt!`);
        } catch (error) {
            console.error('Parse error:', error);
            toast.error(t('parseFailed'));
        }
        
        e.target.value = '';
    };

    const toggleFieldSelection = (index) => {
        const newFields = [...parsedFields];
        newFields[index].selected = !newFields[index].selected;
        setParsedFields(newFields);
    };

    const applySelectedFields = () => {
        // Get only selected fields
        const selectedFields = parsedFields.filter(f => f.selected);
        
        // Build template - only replace selected fields with placeholders
        let template = originalTemplate;
        for (const field of selectedFields) {
            const varName = field.name;
            // Replace value with placeholder
            if (field.type === 'string') {
                template = template.replace(
                    new RegExp(`(Config\\.${varName}\\s*=\\s*)["\'][^"\']*["\']`, 'g'),
                    `$1{{${varName}}}`
                );
            } else if (field.type === 'number') {
                template = template.replace(
                    new RegExp(`(Config\\.${varName}\\s*=\\s*)\\d+\\.?\\d*`, 'g'),
                    `$1{{${varName}}}`
                );
            } else if (field.type === 'boolean') {
                template = template.replace(
                    new RegExp(`(Config\\.${varName}\\s*=\\s*)(true|false)`, 'gi'),
                    `$1{{${varName}}}`
                );
            }
        }
        
        setConfigForm({
            ...configForm,
            fields: selectedFields.map(({ selected, ...f }) => f),
            template: template
        });
        
        setShowFieldSelector(false);
        setParsedFields([]);
        setOriginalTemplate('');
    };

    // Media upload
    const handleMediaUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            await axios.post(`${API}/media`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(t('imageUploaded'));
            fetchData();
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Upload fehlgeschlagen');
        }
    };

    // Copy image as markdown link
    const copyMediaUrl = (media) => {
        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${media.url}`;
        const markdownLink = `![${media.filename}](${imageUrl})`;
        navigator.clipboard.writeText(markdownLink);
        toast.success(t('imageCopied'));
    };

    // Fetch version history for a doc
    const fetchDocHistory = async (doc) => {
        try {
            const response = await axios.get(`${API}/docs/${doc.id}/history`);
            setDocHistory(response.data);
            setSelectedDocForHistory(doc);
            setHistoryDialogOpen(true);
        } catch (error) {
            console.error('Error fetching history:', error);
            toast.error(t('historyFailed'));
        }
    };

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!user?.is_admin) {
        return null;
    }

    const navItems = [
        { id: 'overview', label: t('navOverview'), icon: LayoutDashboard },
        { id: 'projects', label: t('navProjects'), icon: FolderOpen },
        { id: 'docs', label: t('navDocs'), icon: FileText },
        { id: 'configs', label: t('navConfigs'), icon: Cog },
        { id: 'changelog', label: t('navChangelog'), icon: History },
        { id: 'media', label: t('navMedia'), icon: Image },
        { id: 'users', label: t('navUsers'), icon: Users },
    ];

    return (
        <div className="min-h-screen bg-background flex overflow-auto">
            <div className="noise-overlay" />

            {/* Sidebar */}
            <aside className={cn(
                "fixed lg:sticky top-0 left-0 h-screen bg-card border-r border-border z-40 transition-all duration-300",
                sidebarOpen ? "w-64" : "w-0 lg:w-16 overflow-hidden"
            )}>
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="p-4 border-b border-border flex items-center justify-between">
                        {sidebarOpen && (
                            <Link to="/" className="flex items-center gap-2" data-testid="admin-logo">
                                <div className="w-7 h-7 rounded-lg titanv-gradient flex items-center justify-center font-mono font-bold text-white text-xs shadow-lg shadow-primary/30">
                                    &gt;_
                                </div>
                                <span className="font-display font-bold text-base">TitanV</span>
                            </Link>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className="hidden lg:flex"
                        >
                            <ChevronLeft className={cn("w-4 h-4 transition-transform", !sidebarOpen && "rotate-180")} />
                        </Button>
                    </div>

                    {/* Nav */}
                    <ScrollArea className="flex-1 py-4">
                        <nav className="space-y-1 px-2">
                            {navItems.map(item => (
                                <button
                                    key={item.id}
                                    onClick={() => setActiveTab(item.id)}
                                    className={cn(
                                        "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                                        activeTab === item.id
                                            ? "bg-primary/10 text-primary"
                                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                    )}
                                    data-testid={`nav-${item.id}`}
                                >
                                    <item.icon className="w-5 h-5 flex-shrink-0" />
                                    {sidebarOpen && <span>{item.label}</span>}
                                </button>
                            ))}
                        </nav>
                    </ScrollArea>

                    {/* User */}
                    <div className="p-4 border-t border-border">
                        <div className="flex items-center gap-3">
                            {user.avatar ? (
                                <img
                                    src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`}
                                    alt={user.username}
                                    className="w-8 h-8 rounded-full flex-shrink-0"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-primary-foreground flex-shrink-0">
                                    {user.username[0].toUpperCase()}
                                </div>
                            )}
                            {sidebarOpen && (
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{user.username}</p>
                                    <p className="text-xs text-muted-foreground">Admin</p>
                                </div>
                            )}
                        </div>
                        {sidebarOpen && (
                            <div className="flex gap-2 mt-3">
                                <Button variant="outline" size="icon" onClick={toggle} className="flex-1 font-mono text-xs">{lang === 'de' ? 'EN' : 'DE'}</Button>
                                <Button variant="outline" size="icon" onClick={toggleTheme} className="flex-1">
                                    {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                                </Button>
                                <Button variant="outline" size="icon" onClick={logout} className="flex-1">
                                    <LogOut className="w-4 h-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </aside>

            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-background/80 backdrop-blur-sm z-30 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main */}
            <main className="flex-1 min-w-0">
                {/* Mobile header */}
                <div className="lg:hidden sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border p-4 flex items-center justify-between">
                    <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
                        <Menu className="w-5 h-5" />
                    </Button>
                    <span className="font-mono font-bold">Admin</span>
                    <div className="w-10" />
                </div>

                <div className="p-6 lg:p-8">
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <AdminOverview
                            stats={stats}
                            totalViews={totalViews}
                            projects={projects}
                            docs={docs}
                            topDocs={topDocs}
                            feedbackStats={feedbackStats}
                            detailedAnalytics={detailedAnalytics}
                        />
                    )}

                    {/* Projects Tab */}
                    {activeTab === 'projects' && (
                        <AdminProjects
                            projects={projects}
                            docs={docs}
                            onSave={async (form, editing) => {
                                if (editing) {
                                    await axios.put(`${API}/projects/${editing.id}`, form);
                                    toast.success(t('projectUpdated'));
                                } else {
                                    await axios.post(`${API}/projects`, form);
                                    toast.success(t('projectCreated'));
                                }
                                fetchData();
                            }}
                            onDelete={(project) => { setItemToDelete({ type: 'projects', id: project.id, name: project.name }); setDeleteDialogOpen(true); }}
                        />
                    )}

                    {activeTab === 'docs' && (
                        <AdminDocs
                            categories={categories}
                            categoryDialogOpen={categoryDialogOpen}
                            categoryForm={categoryForm}
                            copyFileLink={copyFileLink}
                            createTranslation={createTranslation}
                            docDialogOpen={docDialogOpen}
                            docFiles={docFiles}
                            docForm={docForm}
                            docs={docs}
                            editDoc={editDoc}
                            editingDoc={editingDoc}
                            fetchDocHistory={fetchDocHistory}
                            formatFileSize={formatFileSize}
                            handleCategorySubmit={handleCategorySubmit}
                            handleDocFileDelete={handleDocFileDelete}
                            handleDocFileUpload={handleDocFileUpload}
                            handleDocSubmit={handleDocSubmit}
                            handleDragEnter={handleDragEnter}
                            handleDragLeave={handleDragLeave}
                            handleDragOver={handleDragOver}
                            handleDrop={handleDrop}
                            isDragging={isDragging}
                            navigate={navigate}
                            projects={projects}
                            resetDocForm={resetDocForm}
                            setCategoryDialogOpen={setCategoryDialogOpen}
                            setCategoryForm={setCategoryForm}
                            setDeleteDialogOpen={setDeleteDialogOpen}
                            setDocDialogOpen={setDocDialogOpen}
                            setDocForm={setDocForm}
                            setItemToDelete={setItemToDelete}
                            theme={theme}
                            uploadQueue={uploadQueue}
                            uploadingFile={uploadingFile}
                        />
                    )}

                    {activeTab === 'configs' && (
                        <AdminConfigs
                            addConfigField={addConfigField}
                            applySelectedFields={applySelectedFields}
                            configDialogOpen={configDialogOpen}
                            configForm={configForm}
                            configLanguage={configLanguage}
                            configTemplates={configTemplates}
                            createConfigTranslation={createConfigTranslation}
                            editConfigTemplate={editConfigTemplate}
                            editingConfig={editingConfig}
                            handleConfigSubmit={handleConfigSubmit}
                            parseConfigFile={parseConfigFile}
                            parsedFields={parsedFields}
                            projects={projects}
                            removeConfigField={removeConfigField}
                            resetConfigForm={resetConfigForm}
                            setConfigDialogOpen={setConfigDialogOpen}
                            setConfigForm={setConfigForm}
                            setConfigLanguage={setConfigLanguage}
                            setDeleteDialogOpen={setDeleteDialogOpen}
                            setItemToDelete={setItemToDelete}
                            setParsedFields={setParsedFields}
                            setShowFieldSelector={setShowFieldSelector}
                            showFieldSelector={showFieldSelector}
                            toggleFieldSelection={toggleFieldSelection}
                            updateConfigField={updateConfigField}
                        />
                    )}

                    {/* Media Tab */}
                    {activeTab === 'media' && (
                        <AdminMedia
                            media={media}
                            token={token}
                            onDelete={(m) => { setItemToDelete({ type: 'media', id: m.id, name: m.filename }); setDeleteDialogOpen(true); }}
                            onUploadComplete={fetchData}
                        />
                    )}

                    {/* Users Tab */}
                    {activeTab === 'users' && (
                        <AdminUsers
                            users={users}
                            currentUser={user}
                            token={token}
                            onDelete={(u) => { setItemToDelete({ type: 'users', id: u.id, name: u.username }); setDeleteDialogOpen(true); }}
                            onDataRefresh={fetchData}
                        />
                    )}

                    {activeTab === 'changelog' && (
                        <AdminChangelog
                            changelogDialogOpen={changelogDialogOpen}
                            changelogEntries={changelogEntries}
                            changelogForm={changelogForm}
                            docs={docs}
                            editingChangelog={editingChangelog}
                            fetchData={fetchData}
                            navigate={navigate}
                            setChangelogDialogOpen={setChangelogDialogOpen}
                            setChangelogForm={setChangelogForm}
                            setDeleteDialogOpen={setDeleteDialogOpen}
                            setEditingChangelog={setEditingChangelog}
                            setItemToDelete={setItemToDelete}
                        />
                    )}
                </div>
            </main>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-mono">{t('confirmDelete')}</DialogTitle>
                        <DialogDescription>
                            {t('confirmDeleteText').replace('{{name}}', itemToDelete?.name || '')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>{t('cancel')}</Button>
                        <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
                            <Trash2 className="w-4 h-4 mr-2" />
                            {t('delete')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* History Dialog */}
            <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-mono flex items-center gap-2">
                            <History className="w-5 h-5" />
                            Version History: {selectedDocForHistory?.title}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="max-h-[60vh] overflow-y-auto">
                        {docHistory.length > 0 ? (
                            <div className="space-y-2">
                                {docHistory.map((version, idx) => (
                                    <div 
                                        key={version.id} 
                                        className="p-3 rounded-lg border border-border bg-muted/30 flex items-center justify-between"
                                    >
                                        <div>
                                            <span className="font-mono text-sm font-medium">
                                                Version {version.version_number}
                                            </span>
                                            <p className="text-xs text-muted-foreground">
                                                {new Date(version.created_at).toLocaleString('de-DE')} • von {version.edited_by}
                                            </p>
                                        </div>
                                        <Badge variant="outline" className="text-xs">
                                            {version.title}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground text-center py-8">
                                Keine vorherigen Versionen vorhanden.
                                <br />
                                <span className="text-xs">{t('versionsHint')}</span>
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>{t('close')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
